/**
 * Authenticated file-open helpers (ADR-0008 #44).
 *
 * The desktop opens files in the OS default app; a browser (cloud) can't, so it
 * fetches the bytes over an **authed, tenant-scoped** route and opens a blob URL
 * — a preview for renderable types, a download for the rest. NOTHING here uses
 * the unauthenticated ``/api/files/serve?path`` route (which streams any
 * tenant's exports); that route is being retired.
 */

import { apiFetch, getAccessToken, isNativeHost } from "@/api/client";
import { useChatStore } from "@/stores/chatStore";
import { useToastStore } from "@/components/common/Toast";

// Types the browser renders inline — open these in a new tab (preview). Anything
// else is saved to disk, keeping the artifact's real name + extension.
/** Basename of a filesystem path, POSIX or Windows. */
export function baseName(p: string): string {
  return p.split("/").pop() || p.split("\\").pop() || p;
}

/**
 * Copy natively-picked files into a conversation's workspace and return the
 * copies, keyed by the original path.
 *
 * The OS file dialog hands back real host paths (``/home/me/Downloads/x.csv``).
 * Attaching one of those directly means the file opens on the machine that
 * picked it and NOWHERE else — file sync only replicates the conversation
 * workspace, so the cloud reports "This file has been moved or removed"
 * (reported 2026-07-29). Importing first puts a native attachment on the same
 * footing as a drag-and-drop.
 *
 * Lives here, not in a component, because BOTH composers pick files (the chat
 * input and the home screen) and the first fix only patched one of them — the
 * bug survived and looked identical. One implementation, two callers.
 *
 * Never throws: returns what landed and why the rest didn't, so the caller can
 * tell the user exactly which file failed instead of silently attaching a path
 * that works on one device.
 */
export async function importPickedPaths(
  paths: string[],
  conversationId: string | null,
): Promise<{ imported: Record<string, string>; failed: Record<string, string> }> {
  if (paths.length === 0) return { imported: {}, failed: {} };
  try {
    const data = await apiFetch<{
      imported: Record<string, string>;
      failed: Record<string, string>;
    }>("/files/import", {
      method: "POST",
      body: JSON.stringify({ paths, conversation_id: conversationId ?? "" }),
    });
    return { imported: data.imported ?? {}, failed: data.failed ?? {} };
  } catch (err) {
    console.error("Attachment import failed:", err);
    return {
      imported: {},
      failed: Object.fromEntries(
        paths.map((p) => [p, (err as Error).message || "couldn't be copied"]),
      ),
    };
  }
}

const PREVIEWABLE = new Set(["html", "htm", "pdf", "png", "jpg", "jpeg", "gif", "svg", "webp", "txt", "md"]);

/** Pull the download filename out of a Content-Disposition header, if present. */
function filenameFromDisposition(cd: string | null): string | undefined {
  if (!cd) return undefined;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(cd)?.[1]; // RFC 5987, preferred
  if (star) {
    try {
      return decodeURIComponent(star.replace(/["']/g, ""));
    } catch {
      /* fall through to the plain form */
    }
  }
  return /filename="?([^";]+)"?/i.exec(cd)?.[1];
}

/** Click a detached anchor. Both actions below are anchor-driven: a blob URL has
 *  no name of its own, so a *download* needs ``<a download="…">`` to keep the
 *  file's real name + extension, and a *preview* needs ``target="_blank"`` with
 *  ``rel="noopener noreferrer"`` — see the note in :func:`openBlob` on why this
 *  is an anchor rather than ``window.open``. */
function clickAnchor(attrs: Partial<HTMLAnchorElement> & { href: string }): void {
  const a = document.createElement("a");
  Object.assign(a, attrs);
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Fetch a URL with the bearer token, then EITHER preview it in a new tab (types
 *  the browser renders) OR save it to disk — never both. ``forceDownload`` pins
 *  the second branch; that's the card's explicit "Download". Returns success.
 *
 *  It used to do both at once for every previewable type, because the
 *  popup-blocked probe was:
 *
 *      window.open(objectUrl, "_blank", "noopener,noreferrer") !== null
 *
 *  ``noopener`` makes that call return null *unconditionally* — HTML standard,
 *  window open steps: "If noopener is true or windowType is 'new with no
 *  opener', then return null." So the probe read every successful preview as
 *  blocked and fired the download too: the tab opened AND the file saved. Only
 *  previewable types were hit, since ``PREVIEWABLE.has(ext)`` short-circuits
 *  first — i.e. exactly html / pdf / images. (MDN's page reads ambiguously here;
 *  the spec's step is unambiguous.)
 *
 *  There is no way to keep ``noopener`` *and* detect a blocked popup, so we drop
 *  the detection, not the flag: artifact HTML is LLM-generated and
 *  prompt-injectable (see docs/SECURITY.md on the sandboxed share viewer), and a
 *  blob URL inherits this app's origin — an opened tab that could reach back
 *  through ``window.opener`` would be same-origin with the token store. A
 *  blocked popup surfaces in the browser's own UI, and the card's Download
 *  button remains the deterministic way to get the bytes. */
async function openBlob(url: string, fallbackName?: string, forceDownload = false): Promise<boolean> {
  try {
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${getAccessToken()}` },
    });
    if (!res.ok) return false;
    const name = filenameFromDisposition(res.headers.get("content-disposition")) ?? fallbackName;
    const ext = name?.split(".").pop()?.toLowerCase() ?? "";
    const objectUrl = URL.createObjectURL(await res.blob());
    if (!forceDownload && PREVIEWABLE.has(ext)) {
      clickAnchor({ href: objectUrl, target: "_blank", rel: "noopener noreferrer" });
    } else {
      clickAnchor({ href: objectUrl, download: name || "download", rel: "noopener" });
    }
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    return true;
  } catch {
    return false;
  }
}

/**
 * Open an agent-generated artifact. Native → the OS default app (POST /open);
 * browser → the authed ``/artifacts/{id}/download`` blob (ownership-checked
 * server-side). Falls back to a toast if the browser fetch fails.
 */
export async function openArtifactSmart(artifactId: string, filename?: string): Promise<void> {
  const token = getAccessToken();
  if (isNativeHost()) {
    try {
      const res = await fetch(`/api/shortlists/artifacts/${artifactId}/open`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      // The launcher reports whether it actually dispatched. On ChromeOS Crostini
      // ``xdg-open`` has no MIME handler, so it returns ok:false and the browser
      // must open it — returning unconditionally here made "click artifact →
      // opens in Chrome" silently dead on ChromeOS while it kept working in the
      // cloud web app (regression, 92df88a). We now fall through to the authed
      // blob below rather than the server's ``fallback_url``, which pointed at
      // the ``/api/files/serve?path=`` route RETIRED in Phase D (#44) — so that
      // "recovery" tab had been rendering a 404 body.
      const body = res.ok ? await res.json().catch(() => null) : null;
      if (body?.ok) return;                       // OS app took it
    } catch {
      /* fall through to the browser path */
    }
  }
  const ok = await openBlob(`/api/shortlists/artifacts/${artifactId}/download`, filename);
  if (!ok) {
    useToastStore.getState().add("This file has been moved or removed.", "error");
  }
}

/** Force-save an artifact to disk with its real name + extension (the card's
 *  explicit "Download" action — Open previews renderable types instead). */
export async function downloadArtifactSmart(artifactId: string, filename?: string): Promise<void> {
  const ok = await openBlob(`/api/shortlists/artifacts/${artifactId}/download`, filename, true);
  if (!ok) {
    useToastStore.getState().add("This file has been moved or removed.", "error");
  }
}

/** Resolve an absolute workspace path to (conversationId, rel) under the
 *  ACTIVE conversation's exports dir, or null when it isn't under it. Also
 *  used by the Share action, which targets files by this same pair. */
export function convRelOf(path: string): { conversationId: string; rel: string } | null {
  const convId = useChatStore.getState().activeConversationId;
  const marker = convId ? `/exports/${convId}/` : "";
  const idx = marker ? path.indexOf(marker) : -1;
  if (!convId || idx < 0) return null;
  return { conversationId: convId, rel: path.slice(idx + marker.length) };
}

/** The tenant-scoped serve URL for a workspace path, or null. */
function convServeUrl(path: string): { url: string; name?: string } | null {
  const target = convRelOf(path);
  if (!target) return null;
  return {
    url: `/api/files/conv/${encodeURIComponent(target.conversationId)}/serve?rel=${encodeURIComponent(target.rel)}`,
    name: target.rel.split("/").pop(),
  };
}

/**
 * Open a workspace / tool-referenced file by absolute path. Native → the OS
 * launcher (POST /api/files/open); browser → the authed, tenant-scoped
 * ``/api/files/conv/{id}/serve?rel=…`` blob for files under the ACTIVE
 * conversation's exports dir. No unauthenticated fallback.
 */
export async function openPathSmart(path: string): Promise<void> {
  const token = getAccessToken();
  if (isNativeHost()) {
    try {
      const res = await fetch("/api/files/open", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ path }),
      });
      const body = res.ok ? await res.json().catch(() => null) : null;
      if (body?.ok) return;  // native launcher handled it
      // Launcher couldn't dispatch (ChromeOS Crostini has no MIME handler) — let
      // the browser take it via the authed blob below. We no longer use the
      // server's ``fallback_url``: it points at ``/api/files/serve?path=``,
      // RETIRED in Phase D (#44), so it only ever opened a tab showing a 404.
      // The trade-off is real but small — ``convServeUrl`` resolves only files
      // under the ACTIVE conversation, so a path from elsewhere now toasts
      // "moved or removed" instead of opening. That beats a 404 tab, and the
      // Files panel only ever lists the active conversation's files anyway.
    } catch {
      /* fall through to the browser path */
    }
  }
  const target = convServeUrl(path);
  if (target && (await openBlob(target.url, target.name))) return;
  useToastStore.getState().add("This file has been moved or removed.", "error");
}

/** Force-save a workspace / tool-referenced file to disk, keeping its name. */
export async function downloadPathSmart(path: string): Promise<void> {
  const target = convServeUrl(path);
  if (target && (await openBlob(target.url, target.name, true))) return;
  useToastStore.getState().add("This file has been moved or removed.", "error");
}

/** Reveal an artifact in the OS file manager. Native-only — the UI hides the
 *  affordance in a browser, so there's no browser fallback (and no unauth
 *  ``/api/files/list`` navigation). */
export async function revealArtifact(artifactId: string): Promise<void> {
  await fetch(`/api/shortlists/artifacts/${artifactId}/reveal`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getAccessToken()}` },
  }).catch(() => {});
}

/** Reveal a workspace/tool file in the OS file manager. Native-only. */
export async function revealPath(path: string): Promise<void> {
  await fetch("/api/files/reveal", {
    method: "POST",
    headers: { Authorization: `Bearer ${getAccessToken()}`, "Content-Type": "application/json" },
    body: JSON.stringify({ path }),
  }).catch(() => {});
}
